import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Styles from './styles.PassengersAddressesTour';

/** The tour's steps: the element each one points at (data-tour) and its text */
export const TOUR_STEPS = [
    { target: 'table', titleKey: 'addressesTourTableTitle', textKey: 'addressesTourTableText' },
    { target: 'pills', titleKey: 'addressesTourPassengerTitle', textKey: 'addressesTourPassengerText' },
    { target: 'for-all', titleKey: 'addressesTourForAllTitle', textKey: 'addressesTourForAllText' },
    { target: 'hint', titleKey: 'addressesTourUndoTitle', textKey: 'addressesTourUndoText' },
    { target: 'footer', titleKey: 'addressesTourSaveTitle', textKey: 'addressesTourSaveText' },
] as const;

const BUBBLE_WIDTH = 320;
const GAP = 10;
const EDGE = 8;

interface Rect {
    top: number;
    left: number;
    width: number;
    height: number;
}

interface Props {
    /** The dialog body the targets live in */
    containerRef: React.RefObject<HTMLElement>;
    onClose: () => void;
}

const findTarget = (container: HTMLElement | null, target: string) =>
    container?.querySelector<HTMLElement>(`[data-tour="${target}"]`) || null;

/**
 * A step-by-step tour over the addresses dialog. Opened only from the "guide" button, never by itself (owner,
 * 2026-11-01). Each step rings an element of the real dialog and explains it in a bubble beside it; a step whose
 * element is not on screen (no addresses yet) shows its bubble in the middle.
 */
const PassengersAddressesTour = ({ containerRef, onClose }: Props) => {
    const { t } = useTranslation();
    const [step, setStep] = useState(0);
    const [rect, setRect] = useState<Rect | null>(null);
    const bubbleRef = useRef<HTMLDivElement>(null);

    const { target, titleKey, textKey } = TOUR_STEPS[step];
    const isLast = step === TOUR_STEPS.length - 1;

    const measure = useCallback(() => {
        const element = findTarget(containerRef.current, target);
        if (!element) {
            setRect((prev) => (prev === null ? prev : null));
            return;
        }
        const r = element.getBoundingClientRect();
        const next = r.width || r.height ? { top: r.top, left: r.left, width: r.width, height: r.height } : null;
        // Only a real change re-renders: the tour lives inside the container it observes, so an unconditional
        // update would observe itself and loop
        setRect((prev) =>
            prev && next && prev.top === next.top && prev.left === next.left && prev.width === next.width && prev.height === next.height
                ? prev
                : next
        );
    }, [containerRef, target]);

    useLayoutEffect(() => {
        findTarget(containerRef.current, target)?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
        measure();
    }, [containerRef, target, measure]);

    useEffect(() => {
        window.addEventListener('resize', measure);
        // capture: the table scrolls inside itself, not the window
        window.addEventListener('scroll', measure, true);
        // The dialog's content changes under the tour (addresses arrive, a note appears): measure again, and
        // look for the target again when it was not there yet
        const observer = typeof MutationObserver !== 'undefined' ? new MutationObserver(() => measure()) : null;
        if (observer && containerRef.current) {
            observer.observe(containerRef.current, { childList: true, subtree: true, attributes: true });
        }
        return () => {
            window.removeEventListener('resize', measure);
            window.removeEventListener('scroll', measure, true);
            observer?.disconnect();
        };
    }, [measure, containerRef]);

    // Focus goes back where it was (the guide button) when the tour ends
    useEffect(() => {
        const previous = document.activeElement as HTMLElement | null;
        return () => previous?.focus?.();
    }, []);

    // Each step moves the focus to its bubble, so a keyboard or screen reader user follows the tour
    useEffect(() => {
        bubbleRef.current?.focus();
    }, [step]);

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key !== 'Escape') return;
        // Escape ends the tour, not the addresses dialog around it
        e.stopPropagation();
        onClose();
    };

    const bubblePosition = (): React.CSSProperties => {
        if (!rect) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
        const width = Math.min(BUBBLE_WIDTH, window.innerWidth - 2 * EDGE);
        // Right-aligned with the target (RTL), kept on screen
        const left = Math.max(EDGE, Math.min(rect.left + rect.width - width, window.innerWidth - width - EDGE));
        const below = rect.top + rect.height + GAP;
        // The bubble's real height (it grows with the text size); 200 before its first render
        const height = bubbleRef.current?.offsetHeight || 200;
        // Below the target when there is room for the bubble, else above it, else inside its bottom edge
        if (below + height <= window.innerHeight - EDGE) return { top: below, left, width };
        if (rect.top - GAP - height >= EDGE) return { bottom: window.innerHeight - rect.top + GAP, left, width };
        return { bottom: EDGE, left, width };
    };

    return (
        <>
            {rect && (
                <Styles.Spotlight
                    aria-hidden="true"
                    style={{ top: rect.top - 4, left: rect.left - 4, width: rect.width + 8, height: rect.height + 8 }}
                />
            )}
            {!rect && <Styles.Backdrop aria-hidden="true" />}
            <Styles.Bubble
                ref={bubbleRef}
                role="dialog"
                aria-modal="false"
                aria-labelledby="addresses-tour-title"
                aria-describedby="addresses-tour-text"
                tabIndex={-1}
                style={bubblePosition()}
                onKeyDown={onKeyDown}
                dir="rtl"
            >
                <Styles.StepCount>{t('addressesTourStep', { step: step + 1, total: TOUR_STEPS.length })}</Styles.StepCount>
                <Styles.Title id="addresses-tour-title">{t(titleKey)}</Styles.Title>
                <Styles.Text id="addresses-tour-text">{t(textKey)}</Styles.Text>
                <Styles.Actions>
                    <Styles.Nav>
                        {step > 0 && (
                            <Styles.SecondaryButton type="button" onClick={() => setStep(step - 1)}>
                                {t('addressesTourPrev')}
                            </Styles.SecondaryButton>
                        )}
                        <Styles.PrimaryButton type="button" onClick={isLast ? onClose : () => setStep(step + 1)}>
                            {isLast ? t('addressesTourDone') : t('addressesTourNext')}
                        </Styles.PrimaryButton>
                    </Styles.Nav>
                    {!isLast && (
                        <Styles.TextButton type="button" onClick={onClose}>
                            {t('addressesTourClose')}
                        </Styles.TextButton>
                    )}
                </Styles.Actions>
            </Styles.Bubble>
        </>
    );
};

export default PassengersAddressesTour;
